import { lazy, Suspense, type ReactNode } from 'react';
import { BrowserRouter, Link, Navigate, Route, Routes } from 'react-router';
import { AuthProvider } from './auth.js';
import { Layout, Spinner } from './components.js';
import { ApiTokensPage } from './pages/ApiTokensPage.js';
import { EdgeObjectsPage } from './pages/EdgeObjectsPage.js';
import { AlarmsPage } from './pages/AlarmsPage.js';
import { EdgeUpdatePage } from './pages/EdgeUpdatePage.js';
import { LoginPage } from './pages/LoginPage.js';
import { LorawanPage } from './pages/LorawanPage.js';
import { PasswordPage } from './pages/PasswordPage.js';
import { PointsPage } from './pages/PointsPage.js';
import { SitePage } from './pages/SitePage.js';
import { SitesPage } from './pages/SitesPage.js';
import { TenantsPage } from './pages/TenantsPage.js';
import { UsersPage } from './pages/UsersPage.js';

// The trend page carries uPlot; load it only when a point is opened.
const PointDetailPage = lazy(async () => ({
  default: (await import('./pages/PointDetailPage.js')).PointDetailPage,
}));
// Rules (WP6) are an admin corner; keep them out of the main bundle.
const RulesPage = lazy(async () => ({
  default: (await import('./pages/RulesPage.js')).RulesPage,
}));
// M-Bus and KNX configuration (ADR 0008), admin pages as well.
const MbusPage = lazy(async () => ({
  default: (await import('./pages/MbusPage.js')).MbusPage,
}));
const KnxPage = lazy(async () => ({
  default: (await import('./pages/KnxPage.js')).KnxPage,
}));
// Host firewall (ADR 0009), admin pages.
const FirewallPage = lazy(async () => ({
  default: (await import('./pages/FirewallPage.js')).FirewallPage,
}));
const EdgeFirewallPage = lazy(async () => ({
  default: (await import('./pages/FirewallPage.js')).EdgeFirewallPage,
}));
const RuleEditorPage = lazy(async () => ({
  default: (await import('./pages/RuleEditorPage.js')).RuleEditorPage,
}));

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<Spinner />}>{children}</Suspense>;
}

function NotFound() {
  return (
    <section>
      <h1>Page not found</h1>
      <p className="muted">
        <Link to="/sites">Go to sites</Link>
      </p>
    </section>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider
        fallback={
          <div className="login">
            <Spinner />
          </div>
        }
        signIn={<LoginPage />}
      >
        <Routes>
          <Route element={<Layout />}>
            <Route index element={<Navigate to="/sites" replace />} />
            <Route path="sites" element={<SitesPage />} />
            <Route path="sites/:siteId" element={<SitePage />} />
            <Route path="sites/:siteId/lorawan" element={<LorawanPage />} />
            <Route
              path="sites/:siteId/rules"
              element={
                <Lazy>
                  <RulesPage />
                </Lazy>
              }
            />
            <Route
              path="sites/:siteId/rules/new"
              element={
                <Lazy>
                  <RuleEditorPage />
                </Lazy>
              }
            />
            <Route
              path="sites/:siteId/rules/:ruleId"
              element={
                <Lazy>
                  <RuleEditorPage />
                </Lazy>
              }
            />
            <Route path="edges/:edgeId/objects" element={<EdgeObjectsPage />} />
            <Route
              path="edges/:edgeId/mbus"
              element={
                <Lazy>
                  <MbusPage />
                </Lazy>
              }
            />
            <Route
              path="edges/:edgeId/knx"
              element={
                <Lazy>
                  <KnxPage />
                </Lazy>
              }
            />
            <Route path="points" element={<PointsPage />} />
            <Route path="edges/:edgeId/update" element={<EdgeUpdatePage />} />
            <Route
              path="edges/:edgeId/firewall"
              element={
                <Lazy>
                  <EdgeFirewallPage />
                </Lazy>
              }
            />
            <Route
              path="firewall"
              element={
                <Lazy>
                  <FirewallPage />
                </Lazy>
              }
            />
            <Route path="settings/api-tokens" element={<ApiTokensPage />} />
            <Route path="settings/users" element={<UsersPage />} />
            <Route path="tenants" element={<TenantsPage />} />
            <Route path="settings/password" element={<PasswordPage />} />
            <Route path="alarms" element={<AlarmsPage />} />
            <Route
              path="points/:pointId"
              element={
                <Suspense fallback={<Spinner />}>
                  <PointDetailPage />
                </Suspense>
              }
            />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
